import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Disclosure, DisclosureButton } from "@headlessui/react";
import ReactTimeAgo from "react-time-ago";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";
import { IBreadcrumb } from "../../../../model/interface/breadcrumb";
import { IPermissionGroupAccess } from "../../../../model/interface/permissionGroupAccess";
import { IUserProfileStatus } from "../../../../model/interface/user";
import Breadcrumb from "../../../shared/common/breadcrumb";
import { getPermissionGroupAccessByGroupId } from "../../../../api/permissionGroup.service";
import {
  createUserPermission,
  createUserGroupPermission,
  updateUserPermission,
  getAllUserPermissionsByuserId,
  deleteUserGroupPermission,
  getAuthUserPermissionsByuserId,
} from "../../../../api/userPermission.service";
import { getUserProfileStatus } from "../../../../api/user.service";
import Constant from "../../../../utils/constant";
import {
  AddIcon,
  ApproveIcon,
  DeleteIcon,
  Disapprove,
  CollapseDownIcon,
  CollapseUpIcon,
} from "../../../../assets/icons/index";

import Loader from "../../../shared/loader/loader";
import {
  IAllUserPermission,
  IUserPermission,
} from "../../../../model/interface/userPermission";
import AllowedTo from "../../../shared/common/rbac";
import pagePermissions from "../../../../utils/permissions";
import AccessDenied from "../../../shared/common/accessDenied";
import { useConfirmation } from "../../../shared/confirmation/confirmationService";
import Tooltip from "../../../shared/common/ellipsisText";
import { useNumericParam } from "../../../../utils/useNumericParam";

const ManageUserPermission = () => {
  const id = useNumericParam("id");
  const defaultUserProfile: IUserProfileStatus = {
    userId: id ?? 0,
    firstName: "",
    lastName: "",
    username: "",
    roleName: "",
    roleId: 0,
  };
  const dateOptions = {
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "numeric",
  };
  TimeAgo.addLocale(en);

  type accessType = "VIEW" | "CREATE" | "MODIFY" | "DELETE" | "UPGRADE";
  const confirm = useConfirmation();
  const [loading, setLoading] = useState<boolean>(false);
  const [toggle, setToggle] = useState<number>();
  const [searchFilter, setSearchFilter] = useState("");
  const [userProfile, setUserProfile] = useState(defaultUserProfile);
  const [userPermissions, setUserPermissions] = useState<IAllUserPermission[]>(
    []
  );
  const [groupPermissions, setGroupPermissions] = useState<
    IPermissionGroupAccess[]
  >([]);

  const [authPermissions, setAuthPermissions] = useState<IUserPermission[]>([]);
  const breadcrumbItems = () => {
    const items: Array<IBreadcrumb> = [
      { name: "User Permission", url: Constant.USER_PERMISSIONS },
      { name: "Edit User Permission", url: "" },
    ];

    if (id) {
      items.push({ name: id?.toString(), url: "" });
    }
    return items;
  };

  useEffect(() => {
    getAllUserPermissions();
    getUserProfile();
  }, [id]);

  const getAllUserPermissions = () => {
    setLoading(true);
    getAuthPermissions();
    setToggle(undefined);
    const userId = id;
    if (userId) {
      getAllUserPermissionsByuserId(userId).then((r) => {
        setLoading(false);
        setUserPermissions(r.data);
      });
    }
  };

  const getUserProfile = () => {
    setLoading(true);
    const userId = id;
    if (userId) {
      getUserProfileStatus(userId).then((r) => {
        setLoading(false);
        setUserProfile(r.data);
      });
    }
  };

  const getAuthPermissions = () => {
    setLoading(true);
    const userId = id;
    if (userId) {
      getAuthUserPermissionsByuserId(userId).then((r) => {
        setLoading(false);
        setAuthPermissions(r.data);
      });
    }
  };

  const handleGroupsPermissions = async (id, index?, type?) => {
    setLoading(true);
    if (type === "show") {
      const response = getPermissionGroupAccessByGroupId(id);
      const data = (await response).data;

      setGroupPermissions(data);
      setToggle(index);
    } else {
      setToggle(undefined);
    }
    setLoading(false);
  };

  const saveUserPermission = (permission: any, e: any, type: accessType) => {
    setLoading(true);
    const tmpPermission: IUserPermission = {
      userId: id ?? 0,
      permissionId: permission.permissionId,
      name: "",
      view: type === "VIEW" ? e.target.checked : permission.view,
      create: type === "CREATE" ? e.target.checked : permission.create,
      modify: type === "MODIFY" ? e.target.checked : permission.modify,
      delete: type === "DELETE" ? e.target.checked : permission.delete,
      upgrade: type === "UPGRADE" ? e.target.checked : permission.upgrade,
    };

    if (tmpPermission.create || tmpPermission.delete || tmpPermission.modify) {
      tmpPermission.view = true;
    }

    if (type === "VIEW" && !e.target.checked) {
      tmpPermission.create = false;
      tmpPermission.modify = false;
      tmpPermission.delete = false;
      tmpPermission.view = false;
    }

    if (permission.userId > 0) {
      updateUserPermission(tmpPermission).then((r) => {
        setLoading(false);
        getAllUserPermissions();
      });
    } else {
      createUserPermission(tmpPermission).then((r) => {
        setLoading(false);
        getAllUserPermissions();
      });
    }
  };

  const addUserGroupPermission = (permissionGroupId: number) => {
    setLoading(true);
    const userId = id;
    if (userId) {
      createUserGroupPermission(userId, permissionGroupId).then((res) => {
        setLoading(false);
        getAllUserPermissions();
      });
    }
  };

  const removeUserGroupPermission = async (permissionGroupId: number) => {
    confirm({
      variant: "danger",
      title: "Please Confirm",
      description: "Are you sure you want to delete permission group",
    }).then((d) => {
      setLoading(true);
      const userId = id;
      if (userId) {
        deleteUserGroupPermission(userId, permissionGroupId).then((res) => {
          setLoading(false);
          getAllUserPermissions();
        });
      }
    });
  };

  return (
    <AllowedTo
      perform={[
        pagePermissions.user_permissions_create,
        pagePermissions.user_permissions_modify,
      ]}
      no={() => <AccessDenied />}
    >
      {loading && <Loader />}

      <div className="flex-1 overflow-auto">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8 py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between pb-2">
              <h3 className="text-lg font-light text-gray-900">
                User Permission
              </h3>
            </div>
            <Breadcrumb items={breadcrumbItems()} />

            <div className="flex space-x-8">
              <div className="shadow border border-gray-300 rounded-md mt-4 px-4 py-6 w-4/5">
                <div className="flex justify-between border-b-2 border-gray-300 mb-2">
                  <div>
                    <span className="font-bold text-xl">
                      {userProfile.firstName + " " + userProfile.lastName}
                    </span>
                    <span className="ml-2 font-light text-xs">
                      [{userProfile.username}]
                    </span>
                    <div>
                      <span className="font-semibold">
                        {userProfile.roleName}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span>
                      Last Login Date{" "}
                      {userProfile?.lastLogin &&
                        moment(userProfile.lastLogin).format(
                          "YYYY/MM/DD HH:mm:ss"
                        )}
                    </span>
                    <ReactTimeAgo
                      date={
                        userProfile.lastLogin
                          ? new Date(
                              userProfile?.lastLogin &&
                                moment(userProfile.lastLogin).format(
                                  "YYYY/MM/DD HH:mm:ss"
                                ) + " UTC"
                            )
                          : 1
                      }
                      locale="en-US"
                      className="ml-3 font-light text-xs"
                    />

                    <div>
                      <span>
                        Account Creation{" "}
                        {new Date(
                          userProfile.accountCreationDate
                            ? userProfile.accountCreationDate
                            : ""
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div></div>
                </div>
                <div className="mb-2">
                  <input
                    id="subjectId"
                    name="subjectId"
                    type="text"
                    autoComplete="off"
                    className=" mt-1 order border-gray-300 rounded-md shadow-sm py-2 px-4 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                    placeholder="Search"
                    onChange={(e) => {
                      setSearchFilter(e.target.value);
                    }}
                  />
                </div>
                <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-primary-color">
                      <tr>
                        <th className="px-3 py-2 pl-2 font-light text-sm text-left text-white dark:text-white tracking-wider">
                          <span className="flex gap-2"></span>
                        </th>
                        <th className="px-3 py-2 pl-2 font-light text-sm text-left text-white dark:text-white tracking-wider">
                          <span className="flex gap-2">Name</span>
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 pl-2 font-light text-sm text-left text-white dark:text-white tracking-wider"
                        >
                          <span className="flex gap-2">Description</span>
                        </th>
                        <th className="px-3 py-2 pl-2 font-light text-sm text-left text-white dark:text-white tracking-wider">
                          View
                        </th>
                        <th className="px-3 py-2 pl-2 font-light text-sm text-left text-white dark:text-white tracking-wider">
                          Create
                        </th>
                        <th className="px-3 py-2 pl-2 font-light text-sm text-left text-white dark:text-white tracking-wider">
                          Modify
                        </th>
                        <th className="px-3 py-2 pl-2 font-light text-sm text-left text-white dark:text-white tracking-wider">
                          Delete
                        </th>
                        <th className="px-3 py-2 pl-2 font-light text-sm text-left text-white dark:text-white tracking-wider">
                          Upgrade Required
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {userPermissions
                        .filter((item) => {
                          return (
                            item.permissionName
                              .toUpperCase()
                              .includes(searchFilter.toUpperCase()) ||
                            item.permissionGroupName
                              .toUpperCase()
                              .includes(searchFilter.toUpperCase())
                          );
                        })
                        .map((permission, index) => (
                          <Disclosure key={index}>
                            {({ open }) => (
                              <>
                                <tr
                                  key={index}
                                  className={
                                    index % 2 === 0 ? "bg-white" : "bg-gray-100"
                                  }
                                >
                                  <td className="pl-2 text-sm text-left font-normal text-gray-500 relative">
                                    <div className="flex">
                                      {permission.isGroupedPermission && (
                                        <div className="mt-2">
                                          <DisclosureButton className="">
                                            {index !== toggle && (
                                              <CollapseDownIcon
                                                key={index}
                                                className={`w-5 h-5 text-gray-500`}
                                                onClick={(e) => {
                                                  handleGroupsPermissions(
                                                    permission.permissionGroupId,
                                                    index,
                                                    "show"
                                                  );
                                                }}
                                              />
                                            )}

                                            {index === toggle && (
                                              <CollapseUpIcon
                                                key={index}
                                                className={`w-5 h-5 text-gray-500`}
                                                onClick={(e) => {
                                                  handleGroupsPermissions(
                                                    permission.permissionGroupId,
                                                    index,
                                                    "hide"
                                                  );
                                                }}
                                              />
                                            )}
                                          </DisclosureButton>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 pl-2 whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                    <a className="whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                      {permission.isGroupedPermission
                                        ? permission.permissionGroupName
                                        : permission.permissionName}
                                    </a>
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                    <a className="whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                      <Tooltip
                                        text={permission.description?.replace(
                                          /<[^>]+>/g,
                                          ""
                                        )}
                                      />
                                    </a>
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                    {!permission.isGroupedPermission && (
                                      <input
                                        onClick={(e) => {
                                          saveUserPermission(
                                            permission,
                                            e,
                                            "VIEW"
                                          );
                                        }}
                                        name="viewAccess"
                                        type="checkbox"
                                        className="cursor-pointer"
                                        checked={permission.view}
                                      />
                                    )}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                    {!permission.isGroupedPermission && (
                                      <input
                                        onClick={(e) => {
                                          saveUserPermission(
                                            permission,
                                            e,
                                            "CREATE"
                                          );
                                        }}
                                        name="createAccess"
                                        type="checkbox"
                                        className="cursor-pointer"
                                        checked={permission.create}
                                      />
                                    )}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                    {!permission.isGroupedPermission && (
                                      <input
                                        onClick={(e) => {
                                          saveUserPermission(
                                            permission,
                                            e,
                                            "MODIFY"
                                          );
                                        }}
                                        name="modifyAccess"
                                        type="checkbox"
                                        className="cursor-pointer"
                                        checked={permission.modify}
                                      />
                                    )}
                                  </td>
                                  <td className="px-3 py-2   whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                    {!permission.isGroupedPermission && (
                                      <input
                                        onClick={(e) => {
                                          saveUserPermission(
                                            permission,
                                            e,
                                            "DELETE"
                                          );
                                        }}
                                        name="deleteAccess"
                                        type="checkbox"
                                        className="cursor-pointer"
                                        checked={permission.delete}
                                      />
                                    )}
                                    {permission.isGroupedPermission && (
                                      <div className="text-sm text-center font-normal text-gray-700">
                                        <div className="flex  justify-start">
                                          {permission.userId > 0 && (
                                            <div className="flex gap-3 justify-start">
                                              <ApproveIcon />
                                              <DeleteIcon
                                                className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                                                onClick={() => {
                                                  removeUserGroupPermission(
                                                    permission.permissionGroupId
                                                  );
                                                }}
                                              />
                                            </div>
                                          )}

                                          {permission.userId === 0 && (
                                            <button
                                              type="button"
                                              className=" pr-2 py-1.5 px-2 inline-flex items-center text-xs rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-1 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                                              onClick={() => {
                                                addUserGroupPermission(
                                                  permission.permissionGroupId
                                                );
                                              }}
                                            >
                                              <AddIcon className="w-3 h-3" />
                                              <span>Add</span>
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2   whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                    {!permission.isGroupedPermission && (
                                      <input
                                        onClick={(e) => {
                                          saveUserPermission(
                                            permission,
                                            e,
                                            "UPGRADE"
                                          );
                                        }}
                                        name="upgradeAccess"
                                        type="checkbox"
                                        className="cursor-pointer"
                                        checked={permission.upgrade}
                                      />
                                    )}
                                    {permission.isGroupedPermission && (
                                      <div className="text-sm text-center font-normal text-gray-700">
                                        <div className="flex  justify-start">
                                          {permission.userId > 0 && (
                                            <div className="flex gap-3 justify-start">
                                              <ApproveIcon />
                                              <DeleteIcon
                                                className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                                                onClick={() => {
                                                  removeUserGroupPermission(
                                                    permission.permissionGroupId
                                                  );
                                                }}
                                              />
                                            </div>
                                          )}

                                          {permission.userId === 0 && (
                                            <button
                                              type="button"
                                              className=" pr-2 py-1.5 px-2 inline-flex items-center text-xs rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-1 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                                              onClick={() => {
                                                addUserGroupPermission(
                                                  permission.permissionGroupId
                                                );
                                              }}
                                            >
                                              <AddIcon className="w-3 h-3" />
                                              <span>Add</span>
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                                <tr
                                  className={
                                    index % 2 === 0 ? "bg-white" : "bg-gray-100"
                                  }
                                >
                                  <td colSpan={8} className="relative ">
                                    <div
                                      className={`${
                                        index !== toggle ? "hidden" : ""
                                      } pl-12 pr-12 px-4 pb-2 text-sm text-gray-500`}
                                    >
                                      <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg mt-3">
                                        <table className="min-w-full">
                                          <thead>
                                            <tr className="bg-gray-100">
                                              <th
                                                scope="col"
                                                className="inline-flex items-center gap-2 px-3 py-2 pl-6 font-semibold text-gray-500 text-xs text-left tracking-wider"
                                              >
                                                <span className="flex gap-2">
                                                  Permission Name
                                                </span>
                                              </th>

                                              <th
                                                scope="col"
                                                className="px-3 py-2 pl-6 text-xs text-left text-gray-500 font-semibold tracking-wider"
                                              >
                                                <span className="flex gap-2">
                                                  View
                                                </span>
                                              </th>
                                              <th
                                                scope="col"
                                                className="px-3 py-2 pl-6 text-xs text-left text-gray-500 font-semibold tracking-wider"
                                              >
                                                <span className="flex gap-2">
                                                  Create
                                                </span>
                                              </th>
                                              <th
                                                scope="col"
                                                className="px-3 py-2 pl-6 text-xs text-left text-gray-500 font-semibold tracking-wider"
                                              >
                                                <span className="flex gap-2">
                                                  Modify
                                                </span>
                                              </th>
                                              <th
                                                scope="col"
                                                className="px-3 py-2 pl-6 text-xs text-left text-gray-500 font-semibold tracking-wider"
                                              >
                                                <span className="flex gap-2">
                                                  Delete
                                                </span>
                                              </th>
                                              <th
                                                scope="col"
                                                className="px-3 py-2 pl-6 text-xs text-left text-gray-500 font-semibold tracking-wider"
                                              >
                                                <span className="flex gap-2">
                                                  Upgrade Required
                                                </span>
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y">
                                            {groupPermissions.map(
                                              (permission, index) => (
                                                <tr
                                                  key={index}
                                                  className={"bg-white"}
                                                >
                                                  <td className="px-3 py-2  pl-6 whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                                    {permission.name}
                                                  </td>
                                                  <td className="px-3 py-2 pl-6  text-sm text-center font-normal text-gray-500">
                                                    <div className="flex justify-start">
                                                      {permission.view ? (
                                                        <ApproveIcon />
                                                      ) : (
                                                        <Disapprove />
                                                      )}
                                                    </div>
                                                  </td>
                                                  <td className="px-3 py-2 pl-6  text-sm text-center font-normal text-gray-500">
                                                    <div className="flex justify-start">
                                                      {permission.create ? (
                                                        <ApproveIcon />
                                                      ) : (
                                                        <Disapprove />
                                                      )}
                                                    </div>
                                                  </td>
                                                  <td className="px-3 py-2 pl-6   text-sm text-center font-normal text-gray-500">
                                                    <div className="flex justify-start">
                                                      {permission.modify ? (
                                                        <ApproveIcon />
                                                      ) : (
                                                        <Disapprove />
                                                      )}
                                                    </div>
                                                  </td>
                                                  <td className="px-3 py-2 pl-6  text-sm text-center font-normal text-gray-500">
                                                    <div className="flex justify-start">
                                                      {permission.delete ? (
                                                        <ApproveIcon />
                                                      ) : (
                                                        <Disapprove />
                                                      )}
                                                    </div>
                                                  </td>
                                                  <td className="px-3 py-2 pl-6  text-sm text-center font-normal text-gray-500">
                                                    <div className="flex justify-start">
                                                      {permission.upgrade ? (
                                                        <ApproveIcon />
                                                      ) : (
                                                        <Disapprove />
                                                      )}
                                                    </div>
                                                  </td>
                                                </tr>
                                              )
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                      {groupPermissions.length === 0 && (
                                        <p className="p-2">
                                          No Permissions added
                                        </p>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              </>
                            )}
                          </Disclosure>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="shadow border border-gray-300 rounded-md mt-4 px-4 py-6 w-2/5">
                <div className="text-gray-500 font-semibold">
                  Permission List
                </div>
                <div className="w-full text-gray-500">
                  {authPermissions
                    .filter((item) => item.view)
                    .map((item, index) => (
                      <>
                        {item.view && (
                          <div className=" mt-2">
                            <div className="px-2 py-1 bg-gray-50">
                              <div className="text-sm">view : {item.name}</div>
                            </div>
                          </div>
                        )}

                        {item.create && (
                          <div className=" mt-2">
                            <div className="px-2 py-1 bg-gray-50">
                              <div className="text-sm">
                                create : {item.name}
                              </div>
                            </div>
                          </div>
                        )}

                        {item.modify && (
                          <div className=" mt-2">
                            <div className="px-2 py-1 bg-gray-50">
                              <div className="text-sm">
                                modify : {item.name}
                              </div>
                            </div>
                          </div>
                        )}

                        {item.delete && (
                          <div className=" mt-2">
                            <div className="px-2 py-1 bg-gray-50">
                              <div className="text-sm">
                                delete : {item.name}
                              </div>
                            </div>
                          </div>
                        )}
                        {item.upgrade && (
                          <div className=" mt-2">
                            <div className="px-2 py-1 bg-gray-50">
                              <div className="text-sm">
                                upgrade : {item.name}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};

const mapStateToProps = (state: any) => {
  return {
    profile: state.profile,
  };
};
export default connect(mapStateToProps, {})(ManageUserPermission);
